import {streamWebSocketUrl} from '@/lib/backendUrl';
import {useSnackbar} from 'notistack';
import React from 'react';

import {
    ICEServer,
    IncomingMessage,
    JoinRoom,
    OutgoingMessage,
    RoomCreate,
    RoomInfo,
    UIConfig,
} from './message';
import {loadSettings, resolveCodecPlaceholder} from './settings';
import {authModeToRoomMode} from './useConfig';
import {getFromURL, useRoomID} from './useRoomID';
import {
    DEFAULT_ROOM_DISPLAY_NAME,
    getStoredRoomDisplayName,
    setStoredRoomDisplayName,
} from './roomDisplayName';
import {releaseReceiverAnnotationEngine, clearReceiverAnnotationEngines} from './annotations/receiverAnnotationEngineRegistry';

export type RoomState = false | ConnectedRoom;
export type PauseMode = 'soft' | 'hard';
export type ConnectedRoom = {
    ws: WebSocket;
    hostStream?: MediaStream;
    clientStreams: ClientStream[];
    sharePauseByPeer?: Record<string, {paused: boolean; mode: PauseMode}>;
    /** Room-wide: peer id of the sender whose share is in receiver immersive fullscreen, or null. */
    activeFullscreenPeerId?: string | null;
} & RoomInfo;

interface ClientStream {
    id: string;
    peer_id: string;
    stream: MediaStream;
    /** Sender-created RTCDataChannel `annotations` (JSON ink); wired in {@link clientSession}. */
    annotationChannel?: RTCDataChannel;
}

export interface UseRoom {
    state: RoomState;
    room: FCreateRoom;
    share: () => void;
    setName: (name: string) => void;
    stopShare: () => void;
    setRoomDisplayName: (name: string) => void;
    getPeerConnections: () => Record<string, RTCPeerConnection>;
    togglePauseShare: () => void;
    sharePauseLocal: {isPaused: boolean; pauseMode: PauseMode | null};
    /** Receiver: notify server (and room) when local immersive fullscreen focus changes. */
    reportStreamFullscreen: (userId: string | null) => void;
}

const relayConfig: Partial<RTCConfiguration> =
    window.location.search.indexOf('forceTurn=true') !== -1 ? {iceTransportPolicy: 'relay'} : {};

const hostSession = async ({
    sid,
    ice,
    send,
    done,
    stream,
}: {
    sid: string;
    ice: ICEServer[];
    send: (e: OutgoingMessage) => void;
    done: () => void;
    stream: MediaStream;
}): Promise<RTCPeerConnection> => {
    const peer = new RTCPeerConnection({...relayConfig, iceServers: ice});
    peer.onicecandidate = (event) => {
        if (!event.candidate) {
            return;
        }
        send({type: 'hostice', payload: {sid: sid, value: event.candidate}});
    };

    peer.onconnectionstatechange = (event) => {
        console.log('host change', event);
        if (
            peer.connectionState === 'closed' ||
            peer.connectionState === 'disconnected' ||
            peer.connectionState === 'failed'
        ) {
            peer.close();
            done();
        }
    };

    stream.getTracks().forEach((track) => peer.addTrack(track, stream));

    const preferCodec = resolveCodecPlaceholder(loadSettings().preferCodec);
    if (preferCodec) {
        const transceiver = peer
            .getTransceivers()
            .find((t) => t.sender && t.sender.track === stream.getVideoTracks()[0]);

        if (!!transceiver && 'setCodecPreferences' in transceiver) {
            const exactMatch: RTCRtpCodec[] = [];
            const mimeMatch: RTCRtpCodec[] = [];
            const others: RTCRtpCodec[] = [];

            RTCRtpReceiver.getCapabilities('video')?.codecs.forEach((codec) => {
                if (codec.mimeType === preferCodec.mimeType) {
                    if (codec.sdpFmtpLine === preferCodec.sdpFmtpLine) {
                        exactMatch.push(codec);
                    } else {
                        mimeMatch.push(codec);
                    }
                } else {
                    others.push(codec);
                }
            });

            const sortedCodecs = [...exactMatch, ...mimeMatch, ...others];

            console.log('Setting codec preferences', sortedCodecs);
            transceiver.setCodecPreferences(sortedCodecs);
        }
    }

    const hostOffer = await peer.createOffer({offerToReceiveVideo: true});
    await peer.setLocalDescription(hostOffer);
    send({type: 'hostoffer', payload: {value: hostOffer, sid: sid}});

    return peer;
};

const clientSession = async ({
    sid,
    ice,
    send,
    done,
    onTrack,
    onRemoteShareEnded,
    onAnnotationChannel,
}: {
    sid: string;
    ice: ICEServer[];
    send: (e: OutgoingMessage) => void;
    onTrack: (s: MediaStream) => void;
    done: () => void;
    /** Fires when the sender stops capture (track end / inactive). Faster than waiting for stopshare → endshare. */
    onRemoteShareEnded?: () => void;
    onAnnotationChannel?: (sessionId: string, dc: RTCDataChannel) => void;
}): Promise<RTCPeerConnection> => {
    console.log('ice', ice);
    const peer = new RTCPeerConnection({...relayConfig, iceServers: ice});
    peer.onicecandidate = (event) => {
        if (!event.candidate) {
            return;
        }
        send({type: 'clientice', payload: {sid: sid, value: event.candidate}});
    };
    peer.onconnectionstatechange = (event) => {
        console.log('client change', event);
        if (
            peer.connectionState === 'closed' ||
            peer.connectionState === 'disconnected' ||
            peer.connectionState === 'failed'
        ) {
            peer.close();
            done();
        }
    };

    peer.ondatachannel = (ev) => {
        if (ev.channel.label === 'annotations') {
            onAnnotationChannel?.(sid, ev.channel);
        }
    };

    let notified = false;
    let inactiveHooked = false;
    let remoteEnded = false;
    const fireRemoteEnded = () => {
        if (remoteEnded) {
            return;
        }
        remoteEnded = true;
        onRemoteShareEnded?.();
    };
    const stream = new MediaStream();
    peer.ontrack = (event) => {
        console.log('Received track:', {
            kind: event.track.kind,
            id: event.track.id,
            enabled: event.track.enabled,
            muted: event.track.muted,
            readyState: event.track.readyState,
            label: event.track.label
        });
        
        // Warn if track arrives muted
        if (event.track.muted) {
            console.warn(`⚠️ Track arrived MUTED (${event.track.kind}). This may cause black screen or no audio.`);
        }
        
        stream.addTrack(event.track);
        
        // Ensure track is enabled
        event.track.enabled = true;
        
        event.track.addEventListener('ended', () => {
            console.warn('Track ended:', event.track.kind, event.track.id);
            if (event.track.kind === 'video') {
                fireRemoteEnded();
            }
        });
        
        event.track.onmute = () => {
            console.warn('Track muted:', event.track.kind, event.track.id);
        };
        
        event.track.onunmute = () => {
            console.log('✅ Track unmuted:', event.track.kind, event.track.id);
            // Notify again when track unmutes in case video element needs refresh
            if (event.track.kind === 'video') {
                console.log('Video track is now unmuted - video should appear');
            }
        };
        
        if (!inactiveHooked) {
            inactiveHooked = true;
            stream.addEventListener('inactive', () => fireRemoteEnded());
        }

        if (!notified) {
            notified = true;
            onTrack(stream);
        }
    };

    return peer;
};

export type FCreateRoom = (room: RoomCreate | JoinRoom) => Promise<void>;

export const useRoom = (config: UIConfig, role?: string): UseRoom => {
    const [roomID, setRoomID] = useRoomID({persistInUrl: role !== 'receiver'});
    const {enqueueSnackbar} = useSnackbar();
    const conn = React.useRef<WebSocket | undefined>(undefined);
    const host = React.useRef<Record<string, RTCPeerConnection>>({});
    const client = React.useRef<Record<string, RTCPeerConnection>>({});
    const stream = React.useRef<MediaStream>(undefined);
    /** Sender: true while display capture is active; avoids duplicate stopshare when several track events fire. */
    const sharingActiveRef = React.useRef(false);
    const pendingClientAnnotations = React.useRef<Map<string, RTCDataChannel>>(new Map());

    const [state, setState] = React.useState<RoomState>(false);
    const pauseLocalRef = React.useRef<{isPaused: boolean; pauseMode: PauseMode | null}>({
        isPaused: false,
        pauseMode: null,
    });
    const [sharePauseLocal, setSharePauseLocal] = React.useState<{
        isPaused: boolean;
        pauseMode: PauseMode | null;
    }>({isPaused: false, pauseMode: null});
    const pushedInitialDisplayNameRef = React.useRef(false);

    React.useEffect(() => {
        if (!state) {
            pushedInitialDisplayNameRef.current = false;
        }
    }, [state]);

    const setRoomDisplayName = React.useCallback((name: string) => {
        const trimmed = name.trim().slice(0, 120) || DEFAULT_ROOM_DISPLAY_NAME;
        setStoredRoomDisplayName(trimmed);
        if (conn.current?.readyState === WebSocket.OPEN) {
            conn.current.send(JSON.stringify({type: 'roomdisplayname', payload: {name: trimmed}}));
        }
        setState((current) => (current ? {...current, displayName: trimmed} : current));
    }, []);

    const attachClientAnnotationChannel = React.useCallback((sessionId: string, dc: RTCDataChannel) => {
        setState((current) => {
            if (!current) {
                return current;
            }
            const idx = current.clientStreams.findIndex((s) => s.id === sessionId);
            if (idx === -1) {
                pendingClientAnnotations.current.set(sessionId, dc);
                return current;
            }
            const next = [...current.clientStreams];
            next[idx] = {...next[idx], annotationChannel: dc};
            return {...current, clientStreams: next};
        });
        dc.addEventListener('close', () => {
            setState((current) => {
                if (!current) {
                    return current;
                }
                return {
                    ...current,
                    clientStreams: current.clientStreams.map((s) =>
                        s.id === sessionId ? {...s, annotationChannel: undefined} : s
                    ),
                };
            });
        });
    }, []);

    const room: FCreateRoom = React.useCallback(
        (create) => {
            return new Promise<void>((resolve) => {
                const ws = (conn.current = new WebSocket(streamWebSocketUrl()));
                const send = (message: OutgoingMessage) => {
                    if (ws.readyState === ws.OPEN) ws.send(JSON.stringify(message));
                };
                let first = true;
                ws.onmessage = (data) => {
                    const event: IncomingMessage = JSON.parse(data.data);
                    if (first) {
                        first = false;
                        if (event.type === 'room') {
                            resolve();
                            setState({
                                ws,
                                ...event.payload,
                                clientStreams: [],
                                sharePauseByPeer: {},
                            });
                            setRoomID(event.payload.id);
                        } else {
                            resolve();
                            enqueueSnackbar('Unknown Event: ' + event.type, {variant: 'error'});
                            ws.close(1000, 'received unknown event');
                        }
                        return;
                    }

                    const removeReceiverSessionBySid = (sessionId: string) => {
                        pendingClientAnnotations.current.delete(sessionId);
                        releaseReceiverAnnotationEngine(sessionId);
                        setState((current) => {
                            if (!current) return current;
                            const removed = current.clientStreams.find(({id}) => id === sessionId);
                            if (!removed) return current;
                            removed.stream.getTracks().forEach((t) => t.stop());
                            return {
                                ...current,
                                clientStreams: current.clientStreams.filter(({id}) => id !== sessionId),
                            };
                        });
                        client.current[sessionId]?.close();
                        host.current[sessionId]?.close();
                    };

                    switch (event.type) {
                        case 'room':
                            setState((current) =>
                                current
                                    ? {...current, ...event.payload, sharePauseByPeer: current.sharePauseByPeer ?? {}}
                                    : current
                            );
                            return;
                        case 'sharepausestate': {
                            const {fromUserId, paused, mode} = event.payload;
                            setState((current) => {
                                if (!current) {
                                    return current;
                                }
                                const next = {...(current.sharePauseByPeer ?? {})};
                                if (!paused) {
                                    delete next[fromUserId];
                                } else {
                                    next[fromUserId] = {paused: true, mode: mode === 'hard' ? 'hard' : 'soft'};
                                }
                                return {...current, sharePauseByPeer: next};
                            });
                            return;
                        }
                        case 'streamfullscreenstate': {
                            const raw = event.payload.userId;
                            const next =
                                raw == null || raw === '' ? null : typeof raw === 'string' ? raw : null;
                            setState((current) =>
                                current ? {...current, activeFullscreenPeerId: next} : current
                            );
                            return;
                        }
                        case 'hostsession':
                            if (!stream.current) {
                                return;
                            }
                            hostSession({
                                sid: event.payload.id,
                                stream: stream.current!,
                                ice: event.payload.iceServers,
                                send,
                                done: () => delete host.current[event.payload.id],
                            }).then((peer) => {
                                host.current[event.payload.id] = peer;
                            });
                            return;
                        case 'clientsession':
                            const {id: sid, peer} = event.payload;
                            clientSession({
                                sid,
                                send,
                                ice: event.payload.iceServers,
                                onRemoteShareEnded: () => removeReceiverSessionBySid(sid),
                                onAnnotationChannel: attachClientAnnotationChannel,
                                done: () => {
                                    pendingClientAnnotations.current.delete(sid);
                                    releaseReceiverAnnotationEngine(sid);
                                    delete client.current[sid];
                                    setState((current) =>
                                        current
                                            ? {
                                                  ...current,
                                                  clientStreams: current.clientStreams.filter(
                                                      ({id}) => id !== sid
                                                  ),
                                              }
                                            : current
                                    );
                                },
                                onTrack: (stream) =>
                                    setState((current) => {
                                        if (!current) return current;

                                        const pendingCh = pendingClientAnnotations.current.get(sid);
                                        if (pendingCh) {
                                            pendingClientAnnotations.current.delete(sid);
                                        }

                                        // Check if stream already exists to prevent duplicates
                                        const existingIndex = current.clientStreams.findIndex(
                                            (s) => s.id === sid
                                        );

                                        if (existingIndex >= 0) {
                                            // Update existing stream
                                            console.log(`Updating existing stream ${sid}`);
                                            const updatedStreams = [...current.clientStreams];
                                            const prev = updatedStreams[existingIndex];
                                            updatedStreams[existingIndex] = {
                                                id: sid,
                                                stream,
                                                peer_id: peer,
                                                annotationChannel:
                                                    prev.annotationChannel ?? pendingCh,
                                            };
                                            return {
                                                ...current,
                                                clientStreams: updatedStreams,
                                            };
                                        } else {
                                            // Add new stream
                                            console.log(`Adding new stream ${sid}`);
                                            return {
                                                ...current,
                                                clientStreams: [
                                                    ...current.clientStreams,
                                                    {
                                                        id: sid,
                                                        stream,
                                                        peer_id: peer,
                                                        annotationChannel: pendingCh,
                                                    },
                                                ],
                                            };
                                        }
                                    }),
                            }).then((peer) => (client.current[event.payload.id] = peer));
                            return;
                        case 'clientice':
                            host.current[event.payload.sid]?.addIceCandidate(event.payload.value);
                            return;
                        case 'clientanswer':
                            host.current[event.payload.sid]?.setRemoteDescription(
                                event.payload.value
                            );
                            return;
                        case 'hostoffer':
                            (async () => {
                                await client.current[event.payload.sid]?.setRemoteDescription(
                                    event.payload.value
                                );
                                const answer =
                                    await client.current[event.payload.sid]?.createAnswer();
                                await client.current[event.payload.sid]?.setLocalDescription(
                                    answer
                                );
                                send({
                                    type: 'clientanswer',
                                    payload: {sid: event.payload.sid, value: answer},
                                });
                            })();
                            return;
                        case 'hostice':
                            client.current[event.payload.sid]?.addIceCandidate(event.payload.value);
                            return;
                        case 'endshare':
                            removeReceiverSessionBySid(event.payload);
                            return;
                    }
                };
                ws.onclose = (event) => {
                    if (first) {
                        resolve();
                        first = false;
                    }
                    sharingActiveRef.current = false;
                    pendingClientAnnotations.current.clear();
                    clearReceiverAnnotationEngines();
                    enqueueSnackbar(event.reason, {variant: 'error', persist: true});
                    setState(false);
                };
                ws.onerror = (err) => {
                    if (first) {
                        resolve();
                        first = false;
                    }
                    sharingActiveRef.current = false;
                    pendingClientAnnotations.current.clear();
                    clearReceiverAnnotationEngines();
                    enqueueSnackbar(err?.toString(), {variant: 'error', persist: true});
                    setState(false);
                };
                ws.onopen = () => {
                    create.payload.username = loadSettings().name;
                    send(create);
                };
            });
        },
        [setState, enqueueSnackbar, setRoomID, role, attachClientAnnotationChannel]
    );

    React.useEffect(() => {
        if (role !== 'receiver' || !state) {
            return;
        }
        if (pushedInitialDisplayNameRef.current) {
            return;
        }
        const me = state.users.find((u) => u.you);
        if (!me?.owner) {
            pushedInitialDisplayNameRef.current = true;
            return;
        }
        const serverName = state.displayName?.trim();
        if (serverName) {
            pushedInitialDisplayNameRef.current = true;
            return;
        }
        const local = getStoredRoomDisplayName();
        if (local) {
            setRoomDisplayName(local);
        }
        pushedInitialDisplayNameRef.current = true;
    }, [role, state, setRoomDisplayName]);

    const share = async () => {
        if (role === 'receiver') {
            enqueueSnackbar('Receivers cannot share their screen', {variant: 'error'});
            return;
        }
        if (!navigator.mediaDevices) {
            enqueueSnackbar(
                'Could not start presentation. Are you using https? (mediaDevices undefined)',
                {variant: 'error', persist: true}
            );
            return;
        }
        if (typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
            enqueueSnackbar(
                `Could not start presentation. Your browser likely doesn't support screensharing. (mediaDevices.getDeviceMedia ${typeof navigator.mediaDevices.getDisplayMedia})`,
                {variant: 'error', persist: true}
            );
            return;
        }
        try {
            stream.current = await navigator.mediaDevices.getDisplayMedia({
                video: {frameRate: loadSettings().framerate},
                audio: {
                    echoCancellation: false,
                    autoGainControl: false,
                    noiseSuppression: false,
                    // https://medium.com/@trystonperry/why-is-getdisplaymedias-audio-quality-so-bad-b49ba9cfaa83
                    // @ts-expect-error
                    googAutoGainControl: false,
                },
            });
        } catch (e) {
            console.log('Could not getDisplayMedia', e);
            enqueueSnackbar(`Could not start presentation. (getDisplayMedia error). ${e}`, {
                variant: 'error',
                persist: true,
            });
            return;
        }

        // CRITICAL FIX: Ensure all tracks are enabled and not muted on sender side
        const videoTracks = stream.current?.getVideoTracks() || [];
        const audioTracks = stream.current?.getAudioTracks() || [];
        
        console.log('📤 Starting screen share - checking track states:');
        
        // Force enable all video tracks
        let hasMutedVideo = false;
        videoTracks.forEach((track, i) => {
            const wasMuted = track.muted;
            track.enabled = true;
            console.log(`  Video track ${i}:`, {
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
                label: track.label
            });
            if (wasMuted) {
                hasMutedVideo = true;
                console.warn(`  ⚠️ Video track ${i} was muted - this will cause black screen for receivers!`);
            }
        });
        
        // Warn user if video track is muted
        if (hasMutedVideo) {
            enqueueSnackbar(
                '⚠️ Video track is muted! Receivers may see a black screen. Try stopping and sharing again, or check Screen Recording permissions.',
                {variant: 'warning', autoHideDuration: 8000}
            );
        }
        
        // Force enable all audio tracks
        audioTracks.forEach((track, i) => {
            const wasMuted = track.muted;
            track.enabled = true;
            console.log(`  Audio track ${i}:`, {
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
                label: track.label
            });
            if (wasMuted) {
                console.warn(`  ⚠️ Audio track ${i} was muted - receivers won't hear audio!`);
            }
        });

        sharingActiveRef.current = true;
        const mediaStream = stream.current;
        mediaStream?.getTracks().forEach((track) => {
            track.addEventListener('ended', () => stopShare());
        });
        mediaStream?.addEventListener('inactive', () => stopShare());
        setState((current) => (current ? {...current, hostStream: stream.current} : current));

        conn.current?.send(JSON.stringify({type: 'share', payload: {}}));
    };

    const stopShare = async () => {
        const notify = sharingActiveRef.current;
        sharingActiveRef.current = false;
        pauseLocalRef.current = {isPaused: false, pauseMode: null};
        setSharePauseLocal({isPaused: false, pauseMode: null});
        if (conn.current?.readyState === WebSocket.OPEN) {
            conn.current.send(JSON.stringify({type: 'sharepause', payload: {paused: false}}));
        }
        if (notify && conn.current?.readyState === WebSocket.OPEN) {
            conn.current.send(JSON.stringify({type: 'stopshare', payload: {}}));
        }
        Object.values(host.current).forEach((peer) => {
            peer.close();
        });
        host.current = {};
        stream.current?.getTracks().forEach((track) => track.stop());
        stream.current = undefined;
        setState((current) => (current ? {...current, hostStream: undefined} : current));
    };

    const setName = (name: string): void => {
        conn.current?.send(JSON.stringify({type: 'name', payload: {username: name}}));
    };

    const togglePauseShare = React.useCallback(() => {
        const media = stream.current;
        const videoTrack = media?.getVideoTracks()[0];
        if (!videoTrack) {
            return;
        }

        const local = pauseLocalRef.current;
        if (!local.isPaused) {
            videoTrack.enabled = false;
            media?.getAudioTracks().forEach((t) => {
                t.enabled = false;
            });
            pauseLocalRef.current = {isPaused: true, pauseMode: 'soft'};
            setSharePauseLocal({isPaused: true, pauseMode: 'soft'});
            if (conn.current?.readyState === WebSocket.OPEN) {
                conn.current.send(JSON.stringify({type: 'sharepause', payload: {paused: true, mode: 'soft'}}));
            }
            return;
        }

        videoTrack.enabled = true;
        media?.getAudioTracks().forEach((t) => {
            t.enabled = true;
        });
        pauseLocalRef.current = {isPaused: false, pauseMode: null};
        setSharePauseLocal({isPaused: false, pauseMode: null});
        if (conn.current?.readyState === WebSocket.OPEN) {
            conn.current.send(JSON.stringify({type: 'sharepause', payload: {paused: false}}));
        }
    }, []);

    const getPeerConnections = React.useCallback((): Record<string, RTCPeerConnection> => {
        if (role === 'receiver') {
            return {...client.current};
        }
        return {...host.current};
    }, [role]);

    const reportStreamFullscreen = React.useCallback((userId: string | null) => {
        if (role !== 'receiver') {
            return;
        }
        if (conn.current?.readyState !== WebSocket.OPEN) {
            return;
        }
        conn.current.send(
            JSON.stringify({type: 'reportstreamfullscreen', payload: {userId: userId ?? null}})
        );
    }, [role]);

    React.useEffect(() => {
        if (roomID) {
            const create = getFromURL('create') === 'true';
            
            // Skip if receiver role - RoomManage.tsx handles receiver room creation
            if (role === 'receiver') {
                return;
            }
            
            if (create) {
                const closeOnOwnerLeaveString = getFromURL('closeOnOwnerLeave');
                const closeOnOwnerLeave =
                    closeOnOwnerLeaveString === undefined
                        ? config.closeRoomWhenOwnerLeaves
                        : closeOnOwnerLeaveString === 'true';
                room({
                    type: 'create',
                    payload: {
                        joinIfExist: true,
                        closeOnOwnerLeave,
                        id: roomID,
                        mode: authModeToRoomMode(config.authMode, config.loggedIn),
                        role: role,
                    },
                });
            } else {
                room({type: 'join', payload: {id: roomID, role: role}});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    return {
        state,
        room,
        share,
        stopShare,
        setName,
        setRoomDisplayName,
        getPeerConnections,
        togglePauseShare,
        sharePauseLocal,
        reportStreamFullscreen,
    };
};
